'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@dc-shared-web/components/shadcn/form'
import { Input } from '@dc-shared-web/components/shadcn/input'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { BaseDialog, useCreateFolder } from '@/dc-shared-web/src'
import { usePathname } from 'next/navigation'
import { useTemporaryHighlight } from '@/app/core/hooks/useTemporaryHighlight'

const formSchema = z.object({
  name: z.string().min(1),
})

export function CreateFolderDialog({
  onDialogClose,
  teamUuid,
  parentUuid,
  parentType,
}: {
  onDialogClose: () => void
  teamUuid: string
  parentUuid?: string
  parentType?: 'folder' | 'project'
}) {
  const { mutate: createFolder, isLoading: isCreatingFolder } =
    useCreateFolder()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  })

  const pathname = usePathname()

  const addTempHighlight = useTemporaryHighlight()

  const onCreateSubmit = async (data: z.infer<typeof formSchema>) => {
    return await createFolder(
      {
        name: data.name,
        parent_uuid: parentUuid,
        parent_type: parentType,
        office_uuid: teamUuid,
      },
      {
        onSuccess: (data) => {
          addTempHighlight('newFolderId', data.uuid, pathname)
          onDialogClose()
        },
      }
    )
  }

  return (
    <BaseDialog
      title='New folder'
      isOpen
      onClose={onDialogClose}
      primaryActionSlot={
        <Button
          disabled={!form.formState.isValid || isCreatingFolder}
          type='submit'
          data-testid='create-folder-confirm-btn'
          onClick={form.handleSubmit(onCreateSubmit)}
        >
          {isCreatingFolder ? 'Creating...' : 'Create'}
        </Button>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onCreateSubmit)}>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='tw-sr-only'>Folder name</FormLabel>
                <FormControl>
                  <Input
                    placeholder='Enter folder name'
                    {...field}
                    data-testid='folder-name-input'
                    autoFocus
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </BaseDialog>
  )
}
